import { useCallback, useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { Property } from "../../data/models";
import StatsCard from "../../components/StatsCard";
import Header from "@/components/institutionalClient/Header";
import Sidebar from "@/components/institutionalClient/Sidebar";
import { useNavigate } from "react-router-dom";

// Define interface for stat object
interface Stat {
  title: string;
  value: string;
  change: string;
  trend: "up" | "down" | "neutral";
  icon: {
    path: string;
    bgColor: string;
  };
}

interface ClientStats {
  total: number;
  pending: number;
  completed: number;
  inProgress: number;
}

// Define interface for chart data
interface LineChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    fill: boolean;
    borderColor: string;
    tension: number;
  }[];
}

interface PieChartData {
  labels: string[];
  datasets: {
    data: number[];
    backgroundColor: string[];
  }[];
}

// Define interface for user
interface User {
  _id: string;
  fullName: string;
  email: string;
  role: "SUPERVISOR" | "DATA_COLLECTOR" | "ADMIN";
  employeeId?: string;
  createdAt: string;
}

interface CollectorStats {
  assigned: number;
  pending: number;
  completed: number;
  inProgress: number;
}

function useTheme() {
  const [theme, setThemeState] = useState<"light" | "dark">("light");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const storedTheme =
      (localStorage.getItem("theme") as "light" | "dark") || "light";
    setThemeState(storedTheme);
    setMounted(true);
  }, []);

  const setTheme = useCallback((newTheme: "light" | "dark") => {
    setThemeState(newTheme);
    localStorage.setItem("theme", newTheme);
  }, []);

  useEffect(() => {
    if (mounted) {
      document.documentElement.classList.toggle("dark", theme === "dark");
    }
  }, [theme, mounted]);

  return { theme, setTheme, mounted };
}

  // Format currency
 const formatCurrency = (amount) => {
  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "RWF",
    maximumFractionDigits: 0,
  }).format(amount);

  // Remove any currency symbol and replace it with "RWF"
  const numberOnly = formatted.replace(/[^\d,.\s]/g, "").trim();

  return `RWF ${numberOnly}`;
};

const InstitutionalDashboard = () => {
  const { currentUser, logout, token: authToken } = useAuth();
  const { theme, setTheme } = useTheme();
  const [filter, setFilter] = useState<"all" | "pending" | "completed">("all");
  const [clientStats, setClientStats] = useState<Record<string, ClientStats>>(
    {}
  );
  const [properties, setProperties] = useState<Property[]>([]);
  const [dataCollectors, setDataCollectors] = useState<User[]>([]);
  const [stats, setStats] = useState<Stat[]>([]);
  const [lineChartData, setLineChartData] = useState<LineChartData>({
    labels: [],
    datasets: [
      {
        label: "Property Valuations",
        data: [],
        fill: false,
        borderColor: "rgb(79, 70, 229)",
        tension: 0.1,
      },
    ],
  });
  const [pieChartData, setPieChartData] = useState<PieChartData>({
    labels: [],
    datasets: [
      {
        data: [],
        backgroundColor: [],
      },
    ],
  });
  const [teamPerformance, setTeamPerformance] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [collectorStats, setCollectorStats] = useState<
    Record<string, CollectorStats>
  >({});
  const [collectorNameMap, setCollectorNameMap] = useState<
    Record<string, string>
  >({});
  const navigate = useNavigate();

  // Store original fetch function
  const originalFetch = window.fetch;

  // Intercept fetch requests to handle 401 responses
  useEffect(() => {
    const fetchInterceptor = async (...args: Parameters<typeof fetch>) => {
      try {
        const response = await originalFetch(...args);
        
        // Clone response to read it without consuming the stream
        const clonedResponse = response.clone();
        
        // Check for unauthorized response
        if (response.status === 401) {
          console.log('Token expired, logging out...');
          // Clear local storage
          localStorage.removeItem('valuation_token');
          localStorage.removeItem('valuation_user');
          
          // Navigate to login with message
          navigate('/login', { 
            replace: true,
            state: { 
              message: 'Your session has expired. Please login again.',
              sessionExpired: true 
            }
          });
          
          // Still return the original response
          return response;
        }
        
        return response;
      } catch (error) {
        console.error('Fetch error:', error);
        throw error;
      }
    };

    // Override the global fetch
    window.fetch = fetchInterceptor as typeof fetch;

    // Cleanup: restore original fetch
    return () => {
      window.fetch = originalFetch;
    };
  }, [navigate]);

  const handleAction = (action: string) => {
    switch (action) {
      case "view-report":
        alert("Viewing report...");
        break;
      case "view-data":
        alert("Navigating to Data...");
        break;
      default:
        break;
    }
  };

  const apiUrl =
    import.meta.env.VITE_API_BASE_URL || "https://valuate-api.vercel.app/api/v1";

  useEffect(() => {
    fetchDashboardData();
    fetchDataCollectors();
  }, [filter]);

  useEffect(() => {
    if (properties.length > 0 && dataCollectors.length > 0) {
      calculateCollectorStats();
      createCollectorNameMap();
    }
  }, [properties, dataCollectors]);

  const calculateClientStats = () => {
    const stats: Record<string, ClientStats> = {};

    properties.forEach((property) => {
      const clientKey =
        property.viewerOwnerId || property.ownerIdNumber || property.ownerName;

      if (!clientKey) return;

      if (!stats[clientKey]) {
        stats[clientKey] = {
          total: 0,
          pending: 0,
          completed: 0,
          inProgress: 0,
        };
      }

      stats[clientKey].total++;

      const status = property.status?.toUpperCase();

      if (status === "PENDING" || status === "NEW") {
        stats[clientKey].pending++;
      } else if (status === "COMPLETED" || status === "APPROVED") {
        stats[clientKey].completed++;
      } else if (status === "IN_PROGRESS") {
        stats[clientKey].inProgress++;
      }
    });

    setClientStats(stats);
  };

  useEffect(() => {
    if (properties.length > 0) {
      calculateClientStats();
    }
  }, [properties]);

  const fetchDataCollectors = async () => {
    try {
      const token = localStorage.getItem("valuation_token");
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await fetch(`${apiUrl}/users`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired - logout will be handled by the interceptor
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Filter only DATA_COLLECTOR role users
      const collectors = data.data.filter(
        (user: User) => user.role === "DATA_COLLECTOR"
      );
      setDataCollectors(collectors);
    } catch (err) {
      console.error("Error fetching data collectors:", err);
      // Don't set mock data, just log error
      setDataCollectors([]);
    }
  };

  const createCollectorNameMap = () => {
    const map: Record<string, string> = {};
    dataCollectors.forEach((collector) => {
      map[collector._id] = collector.fullName;
    });
    setCollectorNameMap(map);
  };

  const calculateCollectorStats = () => {
    const stats: Record<string, CollectorStats> = {};

    // Initialize stats for each collector
    dataCollectors.forEach((collector) => {
      stats[collector._id] = {
        assigned: 0,
        pending: 0,
        completed: 0,
        inProgress: 0,
      };
    });

    // Count properties for each collector based on collectorId
    properties.forEach((property) => {
      const collectorId = property.collectorId;
      if (collectorId && stats[collectorId]) {
        stats[collectorId].assigned++;

        // Determine status based on property.status
        if (property.status) {
          const status = property.status.toUpperCase();
          if (status === "PENDING" || status === "NEW") {
            stats[collectorId].pending++;
          } else if (status === "COMPLETED" || status === "APPROVED") {
            stats[collectorId].completed++;
          } else if (status === "IN_PROGRESS" || status === "IN_PROGRESS") {
            stats[collectorId].inProgress++;
          }
        }
      }
    });

    setCollectorStats(stats);
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("valuation_token");
      if (!token) {
        // No token found, redirect to login
        navigate('/login', { 
          replace: true,
          state: { 
            message: 'Please login to continue.',
            sessionExpired: true 
          }
        });
        return;
      }

      // Fetch dashboard data
      const response = await fetch(
        `${apiUrl}/properties/institutional/my-properties`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired - logout will be handled by the interceptor
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: any = await response.json();

      // Set properties
      const propertiesData = data.data || [];

      console.log("propertiesData:---", propertiesData)
      setProperties(propertiesData);

      // Calculate stats from properties
      const totalProperties = propertiesData.length;
      const pendingProperties = propertiesData.filter(
        (p: Property) => p.status === "PENDING" || p.status === "NEW"
      ).length;
      const completedProperties = propertiesData.filter(
        (p: Property) => p.status === "COMPLETED" || p.status === "APPROVED"
      ).length;

      // Calculate average property value
      const totalValue = propertiesData.reduce((sum: number, p: Property) => {
        const value = p.valuationDetails?.totalPropertyValue || 0;
        return sum + Number(value);
      }, 0);
      const averageValue =
        totalProperties > 0 ? totalValue / totalProperties : 0;

      setStats([
        {
          title: "Total Properties",
          value: totalProperties.toString(),
          change: "All properties",
          trend: "neutral",
          icon: {
            path: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4",
            bgColor: "bg-blue-500",
          },
        },
        {
          title: "Pending Approval",
          value: pendingProperties.toString(),
          change: "Needs review",
          trend: "up",
          icon: {
            path: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2",
            bgColor: "bg-yellow-500",
          },
        },
        {
          title: "Completed",
          value: completedProperties.toString(),
          change: "Approved",
          trend: "up",
          icon: {
            path: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
            bgColor: "bg-green-500",
          },
        },
        {
          title: "Average Value",
          value: `RWF ${averageValue.toLocaleString("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}`,
          change: "Property value average",
          trend: averageValue > 0 ? "up" : "neutral",
          icon: {
            path: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
            bgColor: "bg-purple-500",
          },
        },
      ]);

      // Set chart data from actual properties
      const monthlyData = calculateMonthlyValuations(propertiesData);
      setLineChartData(monthlyData);

      const propertyTypeData =
        calculatePropertyTypeDistribution(propertiesData);
      setPieChartData(propertyTypeData);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  // Helper function to calculate monthly valuations
  const calculateMonthlyValuations = (
    properties: Property[]
  ): LineChartData => {
    // Group properties by month of creation
    const monthlyCounts: Record<string, number> = {};

    properties.forEach((property) => {
      if (property.createdAt) {
        const date = new Date(property.createdAt);
        const monthYear = date.toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        });

        if (!monthlyCounts[monthYear]) {
          monthlyCounts[monthYear] = 0;
        }
        monthlyCounts[monthYear]++;
      }
    });

    // Sort by date and prepare chart data
    const sortedMonths = Object.keys(monthlyCounts).sort((a, b) => {
      return new Date(a).getTime() - new Date(b).getTime();
    });

    return {
      labels: sortedMonths,
      datasets: [
        {
          label: "Property Valuations",
          data: sortedMonths.map((month) => monthlyCounts[month]),
          fill: false,
          borderColor: "rgb(79, 70, 229)",
          tension: 0.1,
        },
      ],
    };
  };

  // Helper function to calculate property type distribution
  const calculatePropertyTypeDistribution = (
    properties: Property[]
  ): PieChartData => {
    const typeCounts: Record<string, number> = {};

    properties.forEach((property) => {
      const type =
        property.landUse || property.landParcel?.landUse || "Unknown";
      if (!typeCounts[type]) {
        typeCounts[type] = 0;
      }
      typeCounts[type]++;
    });

    const labels = Object.keys(typeCounts);
    const data = labels.map((label) => typeCounts[label]);

    // Generate colors for each type
    const colors = [
      "rgba(79, 70, 229, 0.8)", // Indigo
      "rgba(59, 130, 246, 0.8)", // Blue
      "rgba(16, 185, 129, 0.8)", // Green
      "rgba(245, 158, 11, 0.8)", // Yellow
      "rgba(239, 68, 68, 0.8)", // Red
      "rgba(139, 92, 246, 0.8)", // Purple
    ];

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: colors.slice(0, labels.length),
        },
      ],
    };
  };

  // Helper function to get user initials
  const getUserInitials = (fullName: string) => {
    if (!fullName) return "DC";
    return fullName
      .split(" ")
      .map((name) => name[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Helper function to get collector name for a property
  const getCollectorName = (property: Property): string => {
    // First check if we have collectorId and name in our map
    if (property.collectorId && collectorNameMap[property.collectorId]) {
      return collectorNameMap[property.collectorId];
    }

    // Fallback to assignedCollector from valuationDetails
    if (property.valuationDetails?.assignedCollector) {
      return property.valuationDetails.assignedCollector;
    }

    // Fallback to collector name from property (if it exists)
    if (property.assignedCollector) {
      return property.assignedCollector;
    }

    return "Unassigned";
  };

  const onPropertySelect = (propertyId: string) => {
    navigate(
      `/institutional-client-dashboard/view-data/property/${propertyId}`
    );
  };

  // Handle logout when token expires
  const handleLogout = () => {
    localStorage.removeItem('valuation_token');
    localStorage.removeItem('valuation_user');
    logout();
    navigate('/login', { 
      replace: true,
      state: { 
        message: 'Your session has expired. Please login again.',
        sessionExpired: true 
      }
    });
  };

  // Check token on component mount
  useEffect(() => {
    const token = localStorage.getItem("valuation_token");
    if (!token) {
      handleLogout();
    }
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }
 

  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-md">
        <Header theme={theme} setTheme={setTheme} logout={handleLogout} />
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-red-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error loading dashboard
            </h3>
            <p className="text-sm text-red-700 mt-2">{error}</p>
            <button
              onClick={fetchDashboardData}
              className="mt-3 bg-red-100 text-red-700 px-3 py-1 rounded text-sm hover:bg-red-200"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-white">
      <Header theme={theme} setTheme={setTheme} logout={handleLogout} />

      <div className="flex">
        <Sidebar
          onViewData={() => handleAction("view-data")}
          onViewReport={() => handleAction("view-report")}
        />

        <main className="flex-1 lg:ml-72 md:ml lg:mr-5 mt-5">
          <div className="space-y-6">
            <h1 className="text-2xl font-semibold text-gray-800">
              Dashboard
            </h1>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <StatsCard
                  key={index}
                  title={stat.title}
                  value={stat.value}
                  change={stat.change}
                  trend={stat.trend}
                  icon={stat.icon}
                />
              ))}
            </div>

            {/* Clients Table */}
            <div className="bg-white p-6 rounded-lg shadow-sm mt-8">
              <h3 className="text-lg font-medium text-gray-800 mb-4">
                Our Clients
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Client name
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Client ID
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        UPI
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Location
                      </th>
                      <th
                        scope="col"
                        className=" py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        Open Market Value
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Object.entries(clientStats).map(([clientKey, stats]) => {
                      const clientProperties = properties.filter(
                        (p) =>
                          p.viewerOwnerId === clientKey ||
                          p.ownerIdNumber === clientKey ||
                          p.ownerName === clientKey
                      );

                      const client = clientProperties[0];

                     console.log("client:---", client?.location)

                      return (
                        <tr key={clientKey}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {client?.ownerName || "Unknown Client"}
                            </div>
                            <div className="text-sm text-gray-500">
                              {client?.phone || "No phone"}
                            </div>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {client?.ownerIdNumber || "N/A"}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              {client?.upi || "No UPI"}
                            </span>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-sm">
  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
    {client?.location?.country?.toLowerCase() === "ug"
      ? [
          client?.location?.region,
          client?.location?.subRegion,
          client?.location?.district,
          client?.location?.county,
          client?.location?.subCounty,
          client?.location?.parish,
          client?.location?.village,
        ]
          .filter(Boolean)
          .join(" / ")
      : [
          client?.location?.province,
          client?.location?.district,
          client?.location?.sector,
          client?.location?.cell,
          client?.location?.village,
        ]
          .filter(Boolean)
          .join(" / ")}
  </span>
</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            {formatCurrency(client?.valuationDetails?.openMarketValue)} 
                            </span>
                          </td>
                  
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {dataCollectors.length === 0 &&
                properties.filter((p) => p.ownerIdNumber).length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No data collectors found. All properties are currently
                    unassigned.
                  </div>
                )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default InstitutionalDashboard;